import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient
} from 'react-query';
import {
  openerOrchestratorAxiosApi,
  positionOpenerAxiosApi
} from 'src/Config/axiosApi';
import { ManualConfirmedPair } from '../types/entry.types';
import {
  OPENER_GROUP_POD_STATE_KEY,
  reresolveGroupAccount
} from './useOpenerGroupPods';
import { markGroupAccountUnresolved } from './groupAccountResolution';
import { adaptPair, PairManagerPair } from './useEntry';

/**
 * Drop EVERY cached `entry-rules` list after a pair changed opener group.
 *
 * Each cached list carries the rule's `openerGroupId` as it was when that list
 * was fetched, and `resolveOpenerApiForRule` routes per-rule writes off that
 * value. Leaving the pre-move copies behind is what produced the 2026-08-07
 * incident (a copy cached before the pair was grouped kept routing that rule's
 * writes to the core pod) and it equally strands a post-move rule on its OLD
 * group's feed pod, which answers 404.
 *
 * `removeQueries`, deliberately, NOT `invalidateQueries`:
 * - a bare `invalidateQueries` leaves the stale data in place for the INACTIVE
 *   lists (v3 does not refetch those by default) and `getQueriesData` — what
 *   the router reads — serves cached data regardless of staleness;
 * - `invalidateQueries(..., { refetchInactive: true })` would fix the staleness
 *   but re-fire every cached list at once, including the UNFILTERED one whose
 *   ~9.5k-doc query the opener page goes out of its way never to run (it
 *   starves the core pod).
 *
 * Removing drops the data outright: the mounted list refetches through its own
 * scoped query, and the unmounted ones simply cease to exist.
 */
function dropStaleEntryRuleGroups(qc: QueryClient): void {
  qc.removeQueries('entry-rules');
}

/**
 * Opener pair groups — operator-defined buckets of manual-confirmed pairs
 * used to scope the opener page (and bulk actions) to one group at a time.
 * Group CRUD, pair→group assignment and the category buckets that seed a
 * dynamic group are all owned by the opener-orchestrator
 * (`openerOrchestratorAxiosApi`): nothing this module reads may require the
 * opportunities pod to be running.
 */
export interface OpenerGroupCategoryRule {
  category: string;
  subcategory?: string;
}

export interface OpenerGroup {
  _id: string;
  name: string;
  color?: string;
  sortOrder?: number;
  makerQuietReevalSeconds?: number | null;
  counterStuckMarketHedgeSeconds?: number | null;
  /**
   * Group-level fallback for the counter/hedge price ladder's rung interval, in
   * milliseconds. `null` (or absent) = no ladder — the field has no default
   * anywhere in the chain, so with the rule empty too the counter path stays
   * the pre-ladder one. A rule's own value wins over this. Floor 250 ms.
   * All of that is the specced contract: nothing on `main` reads this yet.
   */
  counterHedgeLadderStepMs?: number | null;
  /**
   * Group-level fallback for the ladder's adverse-move tick threshold. `null`
   * (or absent) is specced to resolve to 2 engine-side; an explicit `0`
   * disables the adverse/favourable branches. A rule's own value wins over
   * this. See `EntryRule.counterHedgeLadderAdverseTicks` for why this is not
   * the engine's existing `adverse_cancel_ticks`.
   */
  counterHedgeLadderAdverseTicks?: number | null;
  /**
   * Group-level placement-gate-disable default (tri-state). `true`/`false`
   * override; `null` (or absent) = inherit the global default. Per-platform:
   * Polymarket maker leg.
   */
  disablePlacementGatePolymarket?: boolean | null;
  /** Group-level placement-gate-disable default for the Kalshi maker leg. */
  disablePlacementGateKalshi?: boolean | null;
  /**
   * Group-level default (tri-state) for "the Polymarket maker leg goes out
   * with `postOnly`". `true`/`false` override; `null` (or absent) = inherit
   * the global default. A rule's own value wins over this.
   */
  polyMakerPostOnly?: boolean | null;
  lockstepMaxAheadBatches?: number | null;
  categoryRule?: OpenerGroupCategoryRule | null;
  dynamic: boolean;
  /**
   * The venue accounts this group trades, chosen INDEPENDENTLY per venue — a
   * group may run Kalshi on one account and Polymarket on another. `null` is
   * the unambiguous unset the backend 409s a start on; it is never a stand-in
   * for a default account, because reading a group against an account it never
   * traded fabricates a position and the operator cannot tell the difference.
   */
  kalshiAccountId: string | null;
  polymarketAccountId: string | null;
  /**
   * Per-group engine feature-flag opt-ins. Only keys in the registry
   * (`useFeatureFlagRegistry`) are accepted; the backend rejects anything else
   * with a 400 and refuses ANY effective change with a 409 while the group is
   * on/panicked, because the set is delivered to the pod at BOOT.
   */
  featureFlags?: Record<string, boolean>;
  /**
   * The post-chain ON set — `group ?? featureFlagDefaults ?? false`, with the
   * global kill switch applied. Computed server-side by the one implementation
   * of the chain; NEVER re-derive it here.
   */
  resolvedFeatureFlags?: string[];
  pairCount: number;
  /**
   * ISO start of the group's earliest event that has NOT started yet, or null
   * when no member pair carries a start date (elections, crypto, anything not
   * tied to a schedule) or every one of them already started.
   *
   * Resolved server-side from the Polymarket side of each pair first — Kalshi's
   * `startDate` can be the trading-open time, hours before kickoff.
   */
  nextEventStartAt?: string | null;
  /**
   * Member pairs whose event has already started and has not settled. `> 0`
   * is what the picker renders as LIVE. Derived from the start times, NOT from
   * the pairs' `liveStatus` — that field is written by the flip-worker in the
   * opportunities pod and would read zero whenever that pod is scaled down.
   */
  liveCount?: number;
  /**
   * Stored "default config" snapshot for the group — captured via
   * `useSaveGroupDefaultConfig` and re-applied to every member rule (config
   * only, never enable/maker toggles) via `useResetGroupDefaultConfig`. Null
   * until the operator saves one.
   */
  defaultConfig?: Record<string, unknown> | null;
  /** ISO timestamp of the last default-config save, or null if none. */
  defaultConfigSavedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOpenerGroupDto {
  name: string;
  /**
   * REQUIRED, and deliberately not defaulted anywhere in the client: the
   * backend refuses a group without both, and a UI that quietly picked one
   * would put a group on an account nobody chose.
   */
  kalshiAccountId: string;
  polymarketAccountId: string;
  color?: string;
  sortOrder?: number;
  makerQuietReevalSeconds?: number | null;
  counterStuckMarketHedgeSeconds?: number | null;
  /** See `OpenerGroup.counterHedgeLadderStepMs`. */
  counterHedgeLadderStepMs?: number | null;
  /** See `OpenerGroup.counterHedgeLadderAdverseTicks`. */
  counterHedgeLadderAdverseTicks?: number | null;
  disablePlacementGatePolymarket?: boolean | null;
  disablePlacementGateKalshi?: boolean | null;
  polyMakerPostOnly?: boolean | null;
  lockstepMaxAheadBatches?: number | null;
  dynamic?: boolean;
  featureFlags?: Record<string, boolean>;
  categoryRule?: OpenerGroupCategoryRule;
}

export type UpdateOpenerGroupDto = Partial<CreateOpenerGroupDto>;

export interface PairCategory {
  category: string;
  subcategory: string | null;
  pairCount: number;
}

/** Real GET /opener-groups wire shape (see GroupsService.list()). */
export interface OpenerGroupsResponse {
  groups: OpenerGroup[];
  ungroupedCount: number;
}

export const OPENER_GROUPS_KEY = ['opener-groups'];

/**
 * The rows, carrying the moment the request that produced them LEFT.
 *
 * Account-pair consumers need to know when the rows they hold were ASKED for
 * — `dataUpdatedAt` is stamped on resolve and cannot say it. The stamp travels
 * INSIDE the cached value rather than beside it: react-query drops the result
 * of a cancelled refetch while its HTTP response still arrives, so anything
 * published from the query function attests to rows nobody ended up holding.
 * Committed together, a discarded fetch's stamp dies with its rows.
 */
export interface OpenerGroupList {
  groups: OpenerGroup[];
  fetchStartedAt: number;
}

/** Query fn behind the group-list hooks (exported for the resolution tests). */
export async function fetchOpenerGroups(): Promise<OpenerGroupList> {
  const fetchStartedAt = Date.now();
  const { data } = await openerOrchestratorAxiosApi.get<
    OpenerGroupsResponse | OpenerGroup[]
  >('/opener-groups');
  // Canonical wire shape is `{ groups, ungroupedCount }`; the bare-array
  // branch is defensive only (a malformed/legacy payload must not crash
  // every consumer that spreads the result).
  const groups = Array.isArray(data) ? data : (data?.groups ?? []);
  return { groups, fetchStartedAt };
}

const OPENER_GROUPS_QUERY_OPTIONS = {
  refetchInterval: 30_000,
  keepPreviousData: true
} as const;

/**
 * The group list AND the moment it was asked for, from ONE observer — the
 * account guard compares the two, and reading them from separate sources is
 * what let a dropped fetch speak for rows it never delivered.
 */
export function useOpenerGroupList() {
  return useQuery<OpenerGroupList>(
    OPENER_GROUPS_KEY,
    fetchOpenerGroups,
    OPENER_GROUPS_QUERY_OPTIONS
  );
}

export function useOpenerGroups() {
  return useQuery<OpenerGroupList, unknown, OpenerGroup[]>(
    OPENER_GROUPS_KEY,
    fetchOpenerGroups,
    { ...OPENER_GROUPS_QUERY_OPTIONS, select: list => list.groups }
  );
}

/**
 * Group CRUD. A 409 (duplicate name / duplicate categoryRule) propagates to
 * the caller via `mutateAsync` so the create/edit dialog can show an inline
 * error; the global MutationCache.onError backstop still sees the failure.
 * Mutating a group can change pair assignments (dynamic category rules,
 * delete-unassigns), so `manual-confirmed-pairs` is invalidated too.
 */
export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: CreateOpenerGroupDto) => {
      const { data } = await openerOrchestratorAxiosApi.post<OpenerGroup>(
        '/opener-groups',
        dto
      );
      return data;
    },
    {
      mutationKey: 'entry:create-group',
      onSuccess: () => {
        qc.invalidateQueries(['opener-groups']);
        qc.invalidateQueries('manual-confirmed-pairs');
        // A category rule bulk-assigns matching UNGROUPED pairs into the new
        // group (`GroupsService.create` → `buildBulkAssignFilter`, which pins
        // `openerGroupId: null`). Leaving the cached lists behind is the SILENT
        // direction: they still say ungrouped, so the write goes to core, which
        // serves it 200 while `refuseOnCore` is off. Exactly 2026-08-07.
        dropStaleEntryRuleGroups(qc);
      }
    }
  );
}

/**
 * Whether this PATCH can change which two venue accounts the group trades.
 * An edit that names neither field cannot move the pair, so it does not need
 * the re-resolution protocol and must not blank the group's accounts on screen.
 */
const touchesVenueAccounts = (dto: UpdateOpenerGroupDto): boolean =>
  'kalshiAccountId' in dto || 'polymarketAccountId' in dto;

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, dto }: { id: string; dto: UpdateOpenerGroupDto }) => {
      const { data } = await openerOrchestratorAxiosApi.patch<OpenerGroup>(
        `/opener-groups/${id}`,
        dto
      );
      return data;
    },
    {
      mutationKey: 'entry:update-group',
      // Editing the pair is a second way to move a group's accounts, and the
      // ids live only on the 30s-polled `opener-groups` row — so it takes the
      // SAME protocol as start/stop (`groupAccountResolution`): mark before the
      // request leaves, mark again once it has settled, and force a re-read
      // that STARTED after that mark. Without it a poll already in flight
      // restores the pre-edit pair, and every ACT/YES/NO cell on the group
      // renders the previous account's REST contracts as this group's own.
      onMutate: ({ id, dto }: { id: string; dto: UpdateOpenerGroupDto }) => {
        if (touchesVenueAccounts(dto)) markGroupAccountUnresolved(id);
      },
      onSuccess: () => {
        qc.invalidateQueries(['opener-groups']);
        qc.invalidateQueries('manual-confirmed-pairs');
        // The rail's feature-flag chips read the pod-state query, not this one,
        // so without this a saved flag change is invisible until the next poll.
        qc.invalidateQueries(OPENER_GROUP_POD_STATE_KEY);
      },
      // Settled, not success: a PATCH that errored may still have written the
      // ids before failing, so the cached row is equally suspect.
      onSettled: (_data, _error, { id, dto }) => {
        if (touchesVenueAccounts(dto)) reresolveGroupAccount(qc, id);
      }
    }
  );
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await openerOrchestratorAxiosApi.delete<{
        deleted: boolean;
        unassigned: number;
      }>(`/opener-groups/${id}`);
      return data;
    },
    {
      mutationKey: 'entry:delete-group',
      onSuccess: () => {
        qc.invalidateQueries(['opener-groups']);
        qc.invalidateQueries('manual-confirmed-pairs');
        // Deleting a group cascades its pairs to ungrouped (`GroupsService`
        // `updateMany({ openerGroupId: group._id }, { $set: null })` — the
        // response's `unassigned` count IS that `modifiedCount`), and tears the
        // group's feed pod down with it. A leftover list still naming the dead
        // group routes writes at an Ingress that no longer exists.
        dropStaleEntryRuleGroups(qc);
      }
    }
  );
}

/**
 * Distinct categories/subcategories across manual-confirmed pairs, with
 * per-bucket pair counts. Dialog-only data — gate with `enabled` so it
 * isn't fetched until the group dialog opens.
 *
 * Reads the opener-orchestrator's own mirror of `/pairs/categories`. The Bucket
 * page is opener management, so it may not depend on the opportunities pod
 * being up: creating a group from a category has to keep working with
 * opportunities at 0 replicas. Opportunities still serves the same path for its
 * own Opportunities/Mapper surfaces.
 */
export function usePairCategories(enabled = true) {
  return useQuery<PairCategory[]>(
    ['pair-categories'],
    async () => {
      const { data } = await openerOrchestratorAxiosApi.get<PairCategory[]>(
        '/pairs/categories'
      );
      return data;
    },
    { enabled, keepPreviousData: true }
  );
}

export function useAssignPairGroup() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      pairId,
      groupId
    }: {
      pairId: string;
      groupId: string | null;
    }) => {
      const { data } = await openerOrchestratorAxiosApi.patch(
        `/pairs/${pairId}/group`,
        { groupId }
      );
      return data;
    },
    {
      mutationKey: 'entry:assign-pair-group',
      onSuccess: () => {
        qc.invalidateQueries('manual-confirmed-pairs');
        qc.invalidateQueries(['opener-groups']);
        dropStaleEntryRuleGroups(qc);
      }
    }
  );
}

/** Backend caps `pairIds` at 100 per request. */
export const BULK_ASSIGN_CHUNK_SIZE = 100;

/**
 * Assign (or unassign with `groupId: null`) many pairs in chunks of 100 —
 * callers pass ONE id array; the chunking happens here, sequentially, and
 * the per-chunk `matched`/`modified` counters are summed into one result.
 */
export async function bulkAssignPairGroup(
  pairIds: string[],
  groupId: string | null
): Promise<{ matched: number; modified: number }> {
  let matched = 0;
  let modified = 0;
  for (let i = 0; i < pairIds.length; i += BULK_ASSIGN_CHUNK_SIZE) {
    const { data } = await openerOrchestratorAxiosApi.patch<{
      matched: number;
      modified: number;
    }>('/pairs/group/bulk', {
      pairIds: pairIds.slice(i, i + BULK_ASSIGN_CHUNK_SIZE),
      groupId
    });
    matched += data.matched;
    modified += data.modified;
  }
  return { matched, modified };
}

export function useBulkAssignPairGroup() {
  const qc = useQueryClient();
  return useMutation(
    ({ pairIds, groupId }: { pairIds: string[]; groupId: string | null }) =>
      bulkAssignPairGroup(pairIds, groupId),
    {
      mutationKey: 'entry:bulk-assign-pair-group',
      onSuccess: () => {
        qc.invalidateQueries('manual-confirmed-pairs');
        qc.invalidateQueries(['opener-groups']);
        dropStaleEntryRuleGroups(qc);
      }
    }
  );
}

/**
 * Hidden manual-confirmed pairs (hidden-from-opener), for the "show hidden"
 * management view. Same pair shape + `adaptPair` flattening as
 * `useManualConfirmedPairs`; gated by `enabled` so it only fetches while
 * that view is open.
 */
export function useHiddenManualConfirmedPairs(enabled: boolean) {
  return useQuery<ManualConfirmedPair[]>(
    ['manual-confirmed-pairs', 'hidden'],
    async () => {
      const { data } = await positionOpenerAxiosApi.get<PairManagerPair[]>(
        '/pairs',
        { params: { status: 'active', hidden: 'only' } }
      );
      return Array.isArray(data) ? data.map(adaptPair) : [];
    },
    { enabled, refetchInterval: 30_000, keepPreviousData: true }
  );
}
